(function () {
  'use strict';

  if (customElements.get('banner-carousel')) return;

  class BannerCarousel extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;

      this.initialized = true;
      this.cleanups = [];
      this.slides = Array.from(this.querySelectorAll('.banner-carousel__slide'));
      this.dots = Array.from(this.querySelectorAll('.banner-carousel__dot'));
      this.viewport = this.querySelector('.banner-carousel__viewport');
      this.toggleButton = this.querySelector('.banner-carousel__toggle');
      this.status = this.querySelector('.banner-carousel__status');
      this.duration = Math.max(2000, parseInt(this.dataset.timeout, 10) || 5000);
      this.current = Math.max(
        0,
        this.slides.findIndex(function (slide) {
          return slide.classList.contains('is-active');
        })
      );
      this.timer = null;
      this.timerStartedAt = 0;
      this.remaining = this.duration;
      this.hovered = false;
      this.isIntersecting = !('IntersectionObserver' in window);
      this.pointerFocus = false;
      this.pointerFocusTimer = null;
      this.swipeStart = null;
      this.suppressClick = false;
      this.suppressClickTimer = null;
      this.preloadHandle = null;
      this.preloadTimer = null;
      this.navigationToken = 0;
      this.isNavigating = false;
      this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.playRequested = this.dataset.autoplay === 'true' && !this.motionQuery.matches;

      this.updatePlayControl();

      if (!this.slides.length) return;

      this.loadSlide(this.slides[this.current]);
      this.updateSlides(this.current, false);

      if (this.slides.length < 2) return;

      this.bindEvents();
      this.observeVisibility();
      this.syncPlayback();
    }

    disconnectedCallback() {
      this.destroy();
    }

    listen(target, eventName, handler, options) {
      target.addEventListener(eventName, handler, options);
      this.cleanups.push(function () {
        target.removeEventListener(eventName, handler, options);
      });
    }

    bindEvents() {
      var self = this;

      if (this.toggleButton) {
        this.listen(this.toggleButton, 'click', function () {
          self.setPlayRequested(!self.playRequested);
        });
      }

      this.dots.forEach(function (dot) {
        self.listen(dot, 'click', function () {
          self.goTo(parseInt(dot.dataset.index, 10), true);
        });

        self.listen(dot, 'keydown', function (event) {
          self.handleDotKeydown(event);
        });
      });

      this.listen(this, 'pointerdown', function () {
        self.pointerFocus = true;
        window.clearTimeout(self.pointerFocusTimer);
        self.pointerFocusTimer = window.setTimeout(function () {
          self.pointerFocus = false;
        }, 1500);
      }, true);

      this.listen(this, 'click', function () {
        window.clearTimeout(self.pointerFocusTimer);
        self.pointerFocusTimer = window.setTimeout(function () {
          self.pointerFocus = false;
        }, 0);
      });

      this.listen(this, 'pointercancel', function () {
        self.pointerFocus = false;
        window.clearTimeout(self.pointerFocusTimer);
      }, true);

      this.listen(this, 'focusin', function (event) {
        var cameFromOutside = !event.relatedTarget || !self.contains(event.relatedTarget);
        if (cameFromOutside && !self.pointerFocus) self.setPlayRequested(false);
      });

      this.listen(this, 'mouseenter', function () {
        self.hovered = true;
        self.syncPlayback();
      });

      this.listen(this, 'mouseleave', function () {
        self.hovered = false;
        self.syncPlayback();
      });

      this.listen(document, 'visibilitychange', function () {
        self.syncPlayback();
      });

      this.listen(document, 'shopify:block:select', function (event) {
        var slide = event.target && event.target.closest
          ? event.target.closest('.banner-carousel__slide')
          : null;

        if (!slide || !self.contains(slide)) return;

        self.setPlayRequested(false);
        self.goTo(parseInt(slide.dataset.index, 10), false);
      });

      this.listen(document, 'shopify:section:unload', function (event) {
        if (event.detail && event.detail.sectionId === self.dataset.sectionId) self.destroy();
      });

      if (this.viewport) {
        this.listen(this.viewport, 'pointerdown', function (event) {
          self.handleSwipeStart(event);
        });
        this.listen(this.viewport, 'pointerup', function (event) {
          self.handleSwipeEnd(event);
        });
        this.listen(this.viewport, 'pointercancel', function () {
          self.swipeStart = null;
        });
        this.listen(this.viewport, 'click', function (event) {
          if (!self.suppressClick) return;
          event.preventDefault();
          event.stopPropagation();
          self.suppressClick = false;
          window.clearTimeout(self.suppressClickTimer);
        }, true);
      }

      var motionHandler = function (event) {
        if (event.matches) self.setPlayRequested(false);
      };

      if (this.motionQuery.addEventListener) {
        this.motionQuery.addEventListener('change', motionHandler);
        this.cleanups.push(function () {
          self.motionQuery.removeEventListener('change', motionHandler);
        });
      } else if (this.motionQuery.addListener) {
        this.motionQuery.addListener(motionHandler);
        this.cleanups.push(function () {
          self.motionQuery.removeListener(motionHandler);
        });
      }
    }

    observeVisibility() {
      var self = this;

      if (!('IntersectionObserver' in window)) {
        this.isIntersecting = true;
        this.scheduleNextImage();
        return;
      }

      this.observer = new IntersectionObserver(function (entries) {
        var entry = entries[0];
        self.isIntersecting = Boolean(entry && entry.isIntersecting && entry.intersectionRatio >= 0.1);
        self.syncPlayback();
        if (self.isIntersecting) self.scheduleNextImage();
      }, { threshold: [0, 0.1, 0.5] });

      this.observer.observe(this);
    }

    handleDotKeydown(event) {
      var targetIndex = null;
      var currentDot = parseInt(event.currentTarget.dataset.index, 10);
      var isRtl = window.getComputedStyle(this).direction === 'rtl';

      if (event.key === 'Home') targetIndex = 0;
      if (event.key === 'End') targetIndex = this.dots.length - 1;
      if (event.key === 'ArrowRight') targetIndex = currentDot + (isRtl ? -1 : 1);
      if (event.key === 'ArrowLeft') targetIndex = currentDot + (isRtl ? 1 : -1);

      if (targetIndex === null) return;

      event.preventDefault();
      targetIndex = this.normalizeIndex(targetIndex);
      this.goTo(targetIndex, true);
      this.dots[targetIndex].focus();
    }

    handleSwipeStart(event) {
      if (event.pointerType && event.pointerType !== 'touch' && event.pointerType !== 'pen') return;

      this.swipeStart = {
        x: event.clientX,
        y: event.clientY,
        id: event.pointerId
      };
    }

    handleSwipeEnd(event) {
      if (!this.swipeStart || this.swipeStart.id !== event.pointerId) return;

      var deltaX = event.clientX - this.swipeStart.x;
      var deltaY = event.clientY - this.swipeStart.y;
      this.swipeStart = null;

      if (Math.abs(deltaX) < 48 || Math.abs(deltaX) <= Math.abs(deltaY) * 1.2) return;

      this.suppressClick = true;
      window.clearTimeout(this.suppressClickTimer);
      this.suppressClickTimer = window.setTimeout(function () {
        this.suppressClick = false;
      }.bind(this), 500);
      this.goTo(this.current + (deltaX < 0 ? 1 : -1), true);
    }

    normalizeIndex(index) {
      return ((index % this.slides.length) + this.slides.length) % this.slides.length;
    }

    goTo(index, announce) {
      var self = this;
      var nextIndex = this.normalizeIndex(index);
      var targetSlide = this.slides[nextIndex];
      var token = ++this.navigationToken;

      if (targetSlide.dataset.loaded === 'true') {
        this.isNavigating = false;
        this.removeAttribute('data-loading');
        this.removeAttribute('aria-busy');
        this.activateSlide(nextIndex, announce);
        return;
      }

      this.isNavigating = true;
      this.dataset.loading = 'true';
      this.setAttribute('aria-busy', 'true');
      this.syncPlayback();

      this.loadSlide(targetSlide, 'high').then(function () {
        if (!self.initialized || token !== self.navigationToken) return;

        self.isNavigating = false;
        self.removeAttribute('data-loading');
        self.removeAttribute('aria-busy');
        self.activateSlide(nextIndex, announce);
      });
    }

    activateSlide(index, announce) {
      this.updateSlides(index, announce);
      this.resetTimer();
      this.scheduleNextImage();
    }

    updateSlides(nextIndex, announce) {
      var self = this;

      this.current = nextIndex;

      this.slides.forEach(function (slide, index) {
        var isActive = index === nextIndex;
        var link = slide.querySelector('.banner-carousel__link');

        slide.classList.toggle('is-active', isActive);
        slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');

        if (isActive) {
          slide.removeAttribute('inert');
        } else {
          slide.setAttribute('inert', '');
        }

        if (link) link.tabIndex = isActive ? 0 : -1;
      });

      this.dots.forEach(function (dot, index) {
        var isActive = index === nextIndex;
        dot.classList.toggle('is-active', isActive);
        dot.tabIndex = isActive ? 0 : -1;

        if (isActive) {
          dot.setAttribute('aria-current', 'true');
        } else {
          dot.removeAttribute('aria-current');
        }
      });

      if (announce && this.status) {
        this.status.textContent = this.slides[nextIndex].dataset.announcement ||
          'Banner ' + (nextIndex + 1) + ' de ' + this.slides.length;
      }
    }

    loadSlide(slide, priority) {
      if (!slide) return Promise.resolve();

      var image = slide.querySelector('img[data-src], img[data-srcset]');
      var loadTimeout;

      if (!image) image = slide.querySelector('img');

      if (image && priority) {
        image.loading = 'eager';
        image.setAttribute('fetchpriority', priority);
      }

      if (slide.dataset.loaded === 'true') return Promise.resolve();
      if (slide.bannerLoadPromise) return slide.bannerLoadPromise;

      if (!image) {
        slide.dataset.loaded = 'true';
        return Promise.resolve();
      }

      slide.dataset.loaded = 'loading';

      slide.bannerLoadPromise = new Promise(function (resolve) {
        var settled = false;

        function finish() {
          if (settled) return;
          settled = true;
          window.clearTimeout(loadTimeout);
          image.removeEventListener('load', finish);
          image.removeEventListener('error', finish);

          if (image.decode && image.complete && image.naturalWidth > 1) {
            image.decode().catch(function () {}).then(resolve);
          } else {
            resolve();
          }
        }

        image.addEventListener('load', finish);
        image.addEventListener('error', finish);
        loadTimeout = window.setTimeout(finish, 10000);

        slide.querySelectorAll('[data-srcset]').forEach(function (element) {
          element.setAttribute('srcset', element.dataset.srcset);
          element.removeAttribute('data-srcset');
        });

        if (image.dataset.src) {
          image.src = image.dataset.src;
          image.removeAttribute('data-src');
        }

        if (image.complete && image.naturalWidth > 1) finish();
      }).then(function () {
        slide.dataset.loaded = 'true';
        slide.bannerLoadPromise = null;
      });

      return slide.bannerLoadPromise;
    }

    scheduleNextImage() {
      var self = this;
      var connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;

      this.cancelScheduledPreload();

      if (!this.isIntersecting || this.slides.length < 2) return;
      if (connection && (connection.saveData || /2g|3g/.test(connection.effectiveType || ''))) return;

      var task = function () {
        var currentImage = self.slides[self.current].querySelector('img');

        self.preloadHandle = null;
        self.preloadTimer = null;

        if (currentImage && !currentImage.complete) {
          self.preloadTimer = window.setTimeout(function () {
            self.scheduleNextImage();
          }, 400);
          return;
        }

        self.loadSlide(self.slides[self.normalizeIndex(self.current + 1)], 'low');
      };

      if ('requestIdleCallback' in window) {
        this.preloadHandle = window.requestIdleCallback(task, { timeout: 1200 });
      } else {
        this.preloadTimer = window.setTimeout(task, 250);
      }
    }

    cancelScheduledPreload() {
      if (this.preloadHandle !== null && 'cancelIdleCallback' in window) {
        window.cancelIdleCallback(this.preloadHandle);
      }
      if (this.preloadTimer !== null) window.clearTimeout(this.preloadTimer);
      this.preloadHandle = null;
      this.preloadTimer = null;
    }

    setPlayRequested(shouldPlay) {
      this.playRequested = Boolean(shouldPlay);
      this.updatePlayControl();
      this.syncPlayback();
    }

    updatePlayControl() {
      this.dataset.playRequested = this.playRequested ? 'true' : 'false';

      if (!this.toggleButton) return;

      var label = this.playRequested
        ? this.toggleButton.dataset.labelPause
        : this.toggleButton.dataset.labelPlay;

      this.toggleButton.setAttribute('aria-label', label);
      this.toggleButton.title = label;
    }

    shouldPlay() {
      return this.playRequested && !this.hovered && !document.hidden && this.isIntersecting && !this.isNavigating;
    }

    syncPlayback() {
      if (!this.slides || this.slides.length < 2) return;

      if (this.shouldPlay()) {
        this.startTimer();
        this.dataset.playback = 'running';
      } else {
        this.pauseTimer();
        this.dataset.playback = 'paused';
      }
    }

    startTimer() {
      var self = this;
      if (this.timer !== null) return;

      if (!Number.isFinite(this.remaining) || this.remaining < 100) this.remaining = this.duration;

      this.timerStartedAt = window.performance.now();
      this.timer = window.setTimeout(function () {
        self.timer = null;
        self.remaining = self.duration;
        self.goTo(self.current + 1, false);
      }, this.remaining);
    }

    pauseTimer() {
      if (this.timer === null) return;

      var elapsed = window.performance.now() - this.timerStartedAt;
      this.remaining = Math.max(100, this.remaining - elapsed);
      window.clearTimeout(this.timer);
      this.timer = null;
    }

    resetTimer() {
      if (this.timer !== null) window.clearTimeout(this.timer);
      this.timer = null;
      this.timerStartedAt = 0;
      this.remaining = this.duration;
      this.syncPlayback();
    }

    destroy() {
      if (!this.initialized) return;

      if (this.timer !== null) window.clearTimeout(this.timer);
      window.clearTimeout(this.pointerFocusTimer);
      window.clearTimeout(this.suppressClickTimer);
      this.cancelScheduledPreload();
      if (this.observer) this.observer.disconnect();

      this.cleanups.forEach(function (cleanup) {
        cleanup();
      });

      this.cleanups = [];
      this.navigationToken += 1;
      this.isNavigating = false;
      this.initialized = false;
      this.timer = null;
    }
  }

  customElements.define('banner-carousel', BannerCarousel);
})();
